import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Text, StyleSheet, View } from 'react-native';

import { BodyText, TextSpace, theme } from '@sg/garnish';

// ────────────────────────────────────────────────────────────────────────────

export const HomeProductNutritionData = (props: HomeProductNutritionDataProps) => {
  const { calories, totalFatG, totalCarbsG, dietaryFiberG, sugarsG, proteinG, co2eKg } = props;

  const { formatMessage } = useIntl();

  return (
    <View style={styles.container}>
      {calories ? (
        <NutritionLabel value={calories} label={formatMessage(messages.calories)} inGrams={false} />
      ) : null}

      {totalFatG ? (
        <NutritionLabel value={totalFatG} label={formatMessage(messages.fat)} inGrams={true} />
      ) : null}

      {totalCarbsG ? (
        <NutritionLabel value={totalCarbsG} label={formatMessage(messages.carbs)} inGrams={true} />
      ) : null}

      {dietaryFiberG ? (
        <NutritionLabel
          value={dietaryFiberG}
          label={formatMessage(messages.fiber)}
          inGrams={true}
        />
      ) : null}

      {sugarsG ? (
        <NutritionLabel value={sugarsG} label={formatMessage(messages.sugars)} inGrams={true} />
      ) : null}

      {proteinG ? (
        <NutritionLabel value={proteinG} label={formatMessage(messages.protein)} inGrams={true} />
      ) : null}

      {co2eKg ? (
        <NutritionLabel value={co2eKg} label={formatMessage(messages.co2eKg)} inGrams={false} />
      ) : null}
    </View>
  );
};

const NutritionLabel = (props: NutritionLabelProps) => {
  const { value, label, inGrams } = props;

  const { formatMessage } = useIntl();

  return (
    <Text>
      <BodyText sizeMatch={['12']} bold>
        {inGrams ? formatMessage(messages.gramValue, { value }) : value}
      </BodyText>

      <TextSpace />

      <BodyText sizeMatch={['12']}>{label}</BodyText>
    </Text>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  carbs: {
    defaultMessage: 'Carbs',
    description: 'Menu | Nutrition | Carbs | Label',
  },
  protein: {
    defaultMessage: 'Protein',
    description: 'Menu | Nutrition | Protein | Label',
  },
  fat: {
    defaultMessage: 'Fat',
    description: 'Menu | Nutrition | Fat | Label',
  },
  calories: {
    defaultMessage: 'Cal',
    description: 'Menu | Nutrition | Calories | Label',
  },
  sugars: {
    defaultMessage: 'Sugars',
    description: 'Menu | Nutrition | Sugars | Label',
  },
  fiber: {
    defaultMessage: 'Fiber',
    description: 'Menu | Nutrition | Fiber | Label',
  },
  co2eKg: {
    defaultMessage: 'CO2e/kg',
    description: 'Menu | Nutrition | CO2e/kg | Label',
  },
  gramValue: {
    defaultMessage: '{value}g',
    description: 'Menu | Nutrition | Gram value',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeProductNutritionDataProps = {
  calories?: number;
  totalFatG?: number;
  totalCarbsG?: number;
  dietaryFiberG?: number;
  sugarsG?: number;
  proteinG?: number;
  co2eKg?: number;
};

type NutritionLabelProps = {
  value: number;
  label: string;
  inGrams: boolean;
};
